/**
 * Voice Ordering Configuration Component
 * Allows store managers to configure voice ordering settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { voiceOrderingService } from '../services/voiceOrdering';
import type { VoiceConfig, JetsonDevice, VoiceOrderingMode, OrderFlowType } from '../types/voiceOrdering';

interface VoiceOrderingConfigProps {
  storeId: string;
  storeName?: string;
  onSave?: (config: VoiceConfig) => void;
}

// Upsell Rule type
interface UpsellRule {
  id: string;
  name: string;
  enabled: boolean;
  triggerCategories: string[];
  suggestCategories: string[];
  suggestItems: string[];
  prompt: string;
  maxPerOrder: number;
  triggerOnComplete?: boolean;
}

const VoiceOrderingConfig: React.FC<VoiceOrderingConfigProps> = ({
  storeId,
  storeName,
  onSave
}) => {
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [devices, setDevices] = useState<JetsonDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'upsells' | 'prompts' | 'devices' | 'phrases'>('general');

  // Upsell rules state
  const [upsellRules, setUpsellRules] = useState<UpsellRule[]>([]);
  const [combosEnabled, setCombosEnabled] = useState(false);
  const [editingRule, setEditingRule] = useState<UpsellRule | null>(null);

  // New device form state
  const [newDevice, setNewDevice] = useState({ name: '', ipAddress: '', port: '8080' });
  const [addingDevice, setAddingDevice] = useState(false);

  // Load configuration
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [configData, devicesData] = await Promise.all([
        voiceOrderingService.getConfig(storeId),
        voiceOrderingService.listDevices(storeId)
      ]);

      setConfig(configData);
      setDevices(devicesData.devices);

      // Load upsell rules from config
      setUpsellRules((configData as any).upsellRules || []);
      setCombosEnabled((configData as any).combosEnabled || false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Save configuration
  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updatedConfig = await voiceOrderingService.updateConfig(storeId, {
        enabled: config.enabled,
        mode: config.mode as VoiceOrderingMode | 'both',
        orderFlow: config.orderFlow,
        upsellAggressiveness: config.upsellAggressiveness,
        greetingPrompt: config.greetingPrompt,
        closingPrompt: config.closingPrompt,
        upsellPrompts: config.upsellPrompts,
        maxConversationTurns: config.maxConversationTurns,
        confirmationRequired: config.confirmationRequired,
        upsellRules: upsellRules,
        combosEnabled: combosEnabled
      } as any);

      setConfig(updatedConfig);
      setSuccess('Configuration saved successfully');
      onSave?.(updatedConfig);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Add new device
  const handleAddDevice = async () => {
    if (!newDevice.name || !newDevice.ipAddress) {
      setError('Device name and IP address are required');
      return;
    }

    try {
      setAddingDevice(true);
      setError(null);

      const device = await voiceOrderingService.registerDevice(storeId, {
        name: newDevice.name,
        ipAddress: newDevice.ipAddress,
        port: parseInt(newDevice.port) || 8080
      } as any);

      setDevices([...devices, device]);
      setNewDevice({ name: '', ipAddress: '', port: '8080' });
      setSuccess('Device registered successfully');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register device');
    } finally {
      setAddingDevice(false);
    }
  };

  // Remove device
  const handleRemoveDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to remove this device?')) return;

    try {
      await voiceOrderingService.unregisterDevice(storeId, deviceId);
      setDevices(devices.filter(d => d.deviceId !== deviceId));
      setSuccess('Device removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove device');
    }
  };

  // Sync device menu
  const handleSyncDevice = async (deviceId: string) => {
    try {
      setError(null);
      await voiceOrderingService.syncDeviceMenu(storeId, deviceId);
      setSuccess('Menu sync initiated');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh devices to update sync status
      const devicesData = await voiceOrderingService.listDevices(storeId);
      setDevices(devicesData.devices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync device');
    }
  };

  // Update config field
  const updateConfig = (field: keyof VoiceConfig, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  // Update upsell prompt
  const updateUpsellPrompt = (index: number, value: string) => {
    if (!config) return;
    const prompts = [...config.upsellPrompts];
    prompts[index] = value;
    setConfig({ ...config, upsellPrompts: prompts });
  };

  // Add upsell prompt
  const addUpsellPrompt = () => {
    if (!config) return;
    setConfig({
      ...config,
      upsellPrompts: [...config.upsellPrompts, '']
    });
  };

  // Remove upsell prompt
  const removeUpsellPrompt = (index: number) => {
    if (!config) return;
    const prompts = config.upsellPrompts.filter((_, i) => i !== index);
    setConfig({ ...config, upsellPrompts: prompts });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Failed to load voice ordering configuration
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Voice Ordering Configuration</h2>
            {storeName && <p className="text-gray-600 mt-1">{storeName}</p>}
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => updateConfig('enabled', e.target.checked)}
                className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['general', 'upsells', 'prompts', 'devices', 'phrases'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mx-6 mt-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      {/* Tab Content */}
      <div className="p-6">
        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voice Ordering Mode
              </label>
              <select
                value={config.mode}
                onChange={(e) => updateConfig('mode', e.target.value as VoiceOrderingMode | 'both')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="both">Both (POS & Drive-Thru)</option>
                <option value="pos">POS Only (Server-Assisted)</option>
                <option value="drive-thru">Drive-Thru Only</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Select which voice ordering modes are available for this store
              </p>
            </div>

            {/* Order Flow */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Flow
              </label>
              <select
                value={config.orderFlow}
                onChange={(e) => updateConfig('orderFlow', e.target.value as OrderFlowType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="direct-kds">Direct to KDS (Automatic)</option>
                <option value="server-review">Server Review Queue</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Direct to KDS sends orders automatically; Server Review requires confirmation
              </p>
            </div>

            {/* Upsell Aggressiveness */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upsell Aggressiveness: {config.upsellAggressiveness}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={config.upsellAggressiveness}
                onChange={(e) => updateConfig('upsellAggressiveness', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Minimal (1-3)</span>
                <span>Moderate (4-6)</span>
                <span>Aggressive (7-10)</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {config.upsellAggressiveness <= 3 && 'Only suggests if no drinks/sides in order'}
                {config.upsellAggressiveness > 3 && config.upsellAggressiveness <= 6 && 'Suggests size upgrades and complementary items'}
                {config.upsellAggressiveness > 6 && 'Suggests premium items, desserts, and seasonal specials'}
              </p>
            </div>

            {/* Max Conversation Turns */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Conversation Turns
              </label>
              <input
                type="number"
                min="5"
                max="50"
                value={config.maxConversationTurns}
                onChange={(e) => updateConfig('maxConversationTurns', parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Maximum exchanges before prompting to complete order
              </p>
            </div>

            {/* Confirmation Required */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.confirmationRequired}
                  onChange={(e) => updateConfig('confirmationRequired', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Require order confirmation before submitting
                </span>
              </label>
              <p className="mt-1 text-sm text-gray-500 ml-6">
                When enabled, customers must confirm their order before it's sent to the kitchen
              </p>
            </div>

            {/* Tax Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="20"
                value={(config.taxRate * 100).toFixed(2)}
                onChange={(e) => updateConfig('taxRate', parseFloat(e.target.value) / 100)}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Tax rate applied to voice orders
              </p>
            </div>
          </div>
        )}

        {/* Upsells Tab */}
        {activeTab === 'upsells' && (
          <div className="space-y-6">
            {/* Combos Toggle */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Combo Suggestions</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Enable to suggest combo upgrades when customers order entrees
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={combosEnabled}
                    onChange={(e) => setCombosEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium text-blue-800">
                    {combosEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Upsell Rules Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Upsell Rules</h3>
                <p className="text-sm text-gray-500">
                  Configure when to suggest additional items based on what customers order
                </p>
              </div>
              <button
                onClick={() => setEditingRule({
                  id: `rule-${Date.now()}`,
                  name: '',
                  enabled: true,
                  triggerCategories: [],
                  suggestCategories: [],
                  suggestItems: [],
                  prompt: '',
                  maxPerOrder: 1
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Add Rule
              </button>
            </div>

            {/* Upsell Rules List */}
            {upsellRules.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No upsell rules configured yet.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add rules to suggest drinks with food, appetizers with beer, etc.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {upsellRules.map((rule) => (
                  <div
                    key={rule.id}
                    className={`p-4 border rounded-lg ${rule.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={(e) => {
                              setUpsellRules(upsellRules.map(r =>
                                r.id === rule.id ? { ...r, enabled: e.target.checked } : r
                              ));
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <div>
                          <p className={`font-medium ${rule.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                            {rule.name || 'Unnamed Rule'}
                          </p>
                          <p className="text-sm text-gray-500">{rule.prompt}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingRule({ ...rule })}
                          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this rule?')) {
                              setUpsellRules(upsellRules.filter(r => r.id !== rule.id));
                            }
                          }}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <div className="text-xs">
                        <span className="text-gray-500">Triggers:</span>
                        {rule.triggerCategories.map((cat, i) => (
                          <span key={i} className="ml-1 px-2 py-1 bg-orange-100 text-orange-800 rounded">
                            {cat}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs ml-4">
                        <span className="text-gray-500">Suggests:</span>
                        {rule.suggestCategories.map((cat, i) => (
                          <span key={i} className="ml-1 px-2 py-1 bg-green-100 text-green-800 rounded">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Edit Rule Modal */}
            {editingRule && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      {upsellRules.find(r => r.id === editingRule.id) ? 'Edit Upsell Rule' : 'Add Upsell Rule'}
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    {/* Rule Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                      <input
                        type="text"
                        value={editingRule.name}
                        onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Suggest drinks with food"
                      />
                    </div>

                    {/* Trigger Categories */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Trigger Categories
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        When customer orders from these categories, this rule will fire
                      </p>
                      <input
                        type="text"
                        value={editingRule.triggerCategories.join(', ')}
                        onChange={(e) => setEditingRule({
                          ...editingRule,
                          triggerCategories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Entrees, Burgers, Sandwiches"
                      />
                    </div>

                    {/* Suggest Categories */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Suggest Categories
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Suggest items from these categories
                      </p>
                      <input
                        type="text"
                        value={editingRule.suggestCategories.join(', ')}
                        onChange={(e) => setEditingRule({
                          ...editingRule,
                          suggestCategories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Drinks, Beverages, Beer"
                      />
                    </div>

                    {/* Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Suggestion Prompt
                      </label>
                      <textarea
                        value={editingRule.prompt}
                        onChange={(e) => setEditingRule({ ...editingRule, prompt: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Would you like something to drink with that?"
                      />
                    </div>

                    {/* Max Per Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Times Per Order
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={editingRule.maxPerOrder}
                        onChange={(e) => setEditingRule({ ...editingRule, maxPerOrder: parseInt(e.target.value) || 1 })}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How many times this rule can trigger during one order
                      </p>
                    </div>

                    {/* Enabled */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingRule.enabled}
                        onChange={(e) => setEditingRule({ ...editingRule, enabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-sm font-medium text-gray-700">
                        Rule is enabled
                      </label>
                    </div>
                  </div>
                  <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                    <button
                      onClick={() => setEditingRule(null)}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!editingRule.name) {
                          setError('Rule name is required');
                          return;
                        }
                        const existingIndex = upsellRules.findIndex(r => r.id === editingRule.id);
                        if (existingIndex >= 0) {
                          setUpsellRules(upsellRules.map(r => r.id === editingRule.id ? editingRule : r));
                        } else {
                          setUpsellRules([...upsellRules, editingRule]);
                        }
                        setEditingRule(null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save Rule
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="space-y-6">
            {/* Greeting Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Greeting Prompt
              </label>
              <textarea
                value={config.greetingPrompt}
                onChange={(e) => updateConfig('greetingPrompt', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Welcome! What can I get for you today?"
              />
              <p className="mt-1 text-sm text-gray-500">
                First message spoken when a voice session starts
              </p>
            </div>

            {/* Closing Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Closing Prompt
              </label>
              <textarea
                value={config.closingPrompt}
                onChange={(e) => updateConfig('closingPrompt', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Thank you for your order!"
              />
              <p className="mt-1 text-sm text-gray-500">
                Message spoken when an order is completed
              </p>
            </div>

            {/* Upsell Prompts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Upsell Prompts
                </label>
                <button
                  onClick={addUpsellPrompt}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Prompt
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Use {'{item}'} for item name and ${'{price}'} for price
              </p>
              <div className="space-y-3">
                {config.upsellPrompts.map((prompt, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <textarea
                      value={prompt}
                      onChange={(e) => updateUpsellPrompt(index, e.target.value)}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Would you like to add {item} for just ${price}?"
                    />
                    <button
                      onClick={() => removeUpsellPrompt(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                      title="Remove prompt"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="space-y-6">
            {/* Add Device Form */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Register New Jetson Device</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Device Name"
                  value={newDevice.name}
                  onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="IP Address"
                  value={newDevice.ipAddress}
                  onChange={(e) => setNewDevice({ ...newDevice, ipAddress: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Port"
                  value={newDevice.port}
                  onChange={(e) => setNewDevice({ ...newDevice, port: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleAddDevice}
                  disabled={addingDevice}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {addingDevice ? 'Adding...' : 'Add Device'}
                </button>
              </div>
            </div>

            {/* Device List */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Registered Devices</h3>
              {devices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No devices registered. Add a Jetson device to enable voice ordering.
                </p>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div
                      key={device.deviceId}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`h-3 w-3 rounded-full ${
                          device.status === 'online' ? 'bg-green-500' :
                          device.status === 'syncing' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <p className="font-medium text-gray-900">{device.name}</p>
                          <p className="text-sm text-gray-500">
                            {device.ipAddress}:{device.port}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          device.status === 'online' ? 'bg-green-100 text-green-800' :
                          device.status === 'syncing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {device.status}
                        </span>
                        {device.menuSyncedAt && (
                          <span className="text-xs text-gray-500">
                            Synced: {new Date(device.menuSyncedAt).toLocaleString()}
                          </span>
                        )}
                        <button
                          onClick={() => handleSyncDevice(device.deviceId)}
                          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-600 rounded"
                        >
                          Sync Menu
                        </button>
                        <button
                          onClick={() => handleRemoveDevice(device.deviceId)}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Phrases Tab */}
        {activeTab === 'phrases' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Pre-recorded Phrases</h3>
              <p className="text-sm text-blue-700">
                Upload audio files for common phrases to improve response time and voice consistency.
                Pre-recorded phrases are used instead of TTS when available.
              </p>
            </div>

            {/* Phrase List */}
            {config.preRecordedPhrases.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No pre-recorded phrases uploaded yet.
              </p>
            ) : (
              <div className="space-y-3">
                {config.preRecordedPhrases.map((phrase) => (
                  <div
                    key={phrase.phraseKey}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{phrase.phraseKey}</p>
                      <p className="text-sm text-gray-500">{phrase.phraseText}</p>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        {phrase.category}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {phrase.s3Url && (
                        <audio controls className="h-8">
                          <source src={phrase.s3Url} type="audio/mpeg" />
                        </audio>
                      )}
                      <button
                        onClick={() => voiceOrderingService.deletePhrase(storeId, phrase.phraseKey)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Form */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Upload New Phrase</h3>
              <p className="text-sm text-gray-500 mb-4">
                Recording and upload functionality will be available in a future update.
                For now, phrases can be managed via the API.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
        <button
          onClick={loadConfig}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default VoiceOrderingConfig;
